import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AnimatePresence } from "motion/react";
import { useIntl } from "react-intl";
import { DoclistOutdatedDialog } from "~/features/chat/components/modals/DoclistOutdatedDialog";
import { useMobile } from "~/hooks/use-mobile";

import {
  getGetPageTextContentQueryKey,
  useGetPageTextContent,
} from "~/api/documents/documents";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { useChatDocumentParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import logger from "~/utils/logger";

import { usePageActions } from "./hooks/usePageActions";
import { usePageNavigation } from "./hooks/usePageNavigation";
import { usePageSelection } from "./hooks/usePageSelection";
import { useTextSnip } from "./hooks/useTextSnip";

import {
  DocViewerList,
  type DocViewerListHandle,
} from "./components/docViewerList";

import { GetDoclist200, GetPageTextContent200 } from "~/api/model";
import { PageItem } from "~/features/chat/hooks/useDocList";
import { usePageAnnotations } from "~/features/pageAnnotations/hooks/usePageAnnotations";
import { DEFAULT_ANNOTATION_COLOR } from "~/features/pageAnnotations/types";
import { useSkyViewStore } from "../../store";
import { DocumentSearchPanel } from "./components/DocumentSearchPanel";
import { PageNumberPanel } from "./components/PageNumberPanel";
import { SelectedPagePanel } from "./components/SelectedPagePanel";

type DocViewerProps = {
  pageItems: PageItem[];
  docList: GetDoclist200;
  isViewOnly: boolean;
};

export const DocViewer = ({
  pageItems,
  docList,
  isViewOnly,
}: DocViewerProps) => {
  const { organizationId, caseId, chatId, documentId } =
    useChatDocumentParams();
  const [searchParams] = useDocumentSearchParams();
  const { project } = useParentCase();
  const { toast } = useToast();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { isMobile } = useMobile();

  const isAddingAnnotation = useSkyViewStore(
    (state) => state.isAddingAnnotation
  );
  const annotationColor = useSkyViewStore((state) => state.annotationColor);
  const setIsAddingAnnotation = useSkyViewStore(
    (state) => state.setIsAddingAnnotation
  );

  const {
    getAnnotationsForPage,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = usePageAnnotations(organizationId, project?.id);

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<DocViewerListHandle>(null);

  const [isDoclistOutdated, setIsDoclistOutdated] = useState(false);
  const [recognizeModalOpen, setRecognizeModalOpen] = useState(false);
  const [isZoomActive, setIsZoomActive] = useState(false);
  const [isSnipActive, setIsSnipActive] = useState(false);
  const [isCropActive, setIsCropActive] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string | undefined>(
    undefined
  );
  const [pageRotation, setPageRotation] = useState(0);

  const [_, updateParams] = useDocumentSearchParams();

  const {
    selectedPage,
    selectedPageId,
    selectedPageNumber,
    togglePageSelection,
    clearSelectedPages,
  } = usePageSelection(pageItems);

  const totalPages = useMemo(() => {
    return pageItems.length;
  }, [pageItems]);

  const modalTitle = useMemo(() => {
    if (!selectedPage) return "Page Content Recognition";

    const section = docList?.tree
      .flatMap((doc) => doc.sections)
      .find((sec) => sec.id === selectedPage.sectionId);

    const sectionName = section?.name || "Unknown Section";

    return `${sectionName} – Page ${selectedPageNumber}`;
  }, [selectedPage, selectedPageNumber, docList]);

  const { currentPageNumber, setCurrentPageNumber } = usePageNavigation({
    pageItems,
  });

  const handleRotateSelectedPage = async () => {
    if (!selectedPage) return;

    try {
      await rotatePage();
    } catch (error) {
      // Handle any errors from the hook
    }
  };

  const handleDeleteSelectedPage = async () => {
    try {
      await deletePage();
      toast({
        title: intl.formatMessage({
          id: "docViewer.deletePage.success",
          defaultMessage: "Page deleted",
        }),
        description: intl.formatMessage({
          id: "docViewer.deletePage.successDescription",
          defaultMessage: "The page has been successfully removed.",
        }),
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docViewer.deletePage.error",
          defaultMessage: "Failed to delete the page. Please try again.",
        }),
      });
    }
  };

  const { rotatePage, deletePage, isRotating } = usePageActions({
    organizationId,
    caseId,
    chatId,
    projectId: project?.id,
    selectedPage,
    clearSelectedPages,
  });

  const {
    data: pageContentData,
    isLoading: isPageContentLoading,
    error: pageContentError,
  } = useGetPageTextContent(
    organizationId,
    project?.id || "",
    selectedPageId ?? "",
    {
      query: {
        enabled: !!selectedPageId,
      },
    }
  );

  const handleRecognizeSelectedPage = async () => {
    if (selectedPage) {
      setIsContentLoading(true);
      setRecognizeModalOpen(true);
      setPageRotation(selectedPage.rotate);

      try {
        const urls = await selectedPage.getUrls();
        setPageImageUrl(urls.retinaUrl || urls.highResUrl || undefined);
      } catch (error) {
        setPageImageUrl(undefined);
      } finally {
        setIsContentLoading(false);
      }
    }
  };

  const handleToggleZoom = () => {
    const nextZoomActive = !isZoomActive;
    setIsZoomActive(nextZoomActive);

    if (nextZoomActive) {
      setIsSnipActive(false);
      cancelSnip();
      setIsCropActive(false);
      setIsAddingAnnotation(false);
    }
  };

  const handleToggleHighlight = () => {
    const nextSnipActive = !isSnipActive;
    setIsSnipActive(nextSnipActive);

    if (nextSnipActive) {
      setIsCropActive(false);
      setIsAddingAnnotation(false);
      cancelSnip();
      return;
    }

    cancelSnip();
  };

  const handleToggleCrop = () => {
    const nextCropActive = !isCropActive;
    setIsCropActive(nextCropActive);

    if (nextCropActive) {
      setIsSnipActive(false);
      cancelSnip();
      setIsAddingAnnotation(false);
      return;
    }
  };

  const textSnipParams = useMemo(() => {
    if (!selectedPage || !project?.id) return null;
    return {
      pageId: selectedPage.pageId,
      organizationId,
      projectId: project.id,
      rotation: selectedPage.rotate,
    };
  }, [selectedPage, organizationId, project?.id]);

  const {
    snipState,
    cancelSnip,
    handlers: snipHandlers,
  } = useTextSnip(textSnipParams);

  const handleDoclistUpdate = () => {
    setIsDoclistOutdated(false);
  };

  const handleContentUpdate = useCallback(
    (newContent: string) => {
      if (selectedPageId && project?.id) {
        const queryKey = getGetPageTextContentQueryKey(
          organizationId,
          project.id,
          selectedPageId
        );
        queryClient.setQueryData<GetPageTextContent200>(queryKey, (oldData) =>
          oldData ? { ...oldData, content: newContent } : undefined
        );
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [queryClient, organizationId, project?.id, selectedPageId]
  );

  const isProgrammaticChange = useRef(false);

  const handleCurrentPageChange = useCallback(
    (index: number) => {
      if (currentPageNumber === index + 1) return;

      isProgrammaticChange.current = true;
      setCurrentPageNumber(index + 1);
    },
    [currentPageNumber, setCurrentPageNumber]
  );

  const handlePageChangeFromPanel = (page: number) => {
    setCurrentPageNumber(page);
  };

  const handleSearchNavigateToPage = (pageId: string) => {
    updateParams({ pageId });
  };

  useEffect(() => {
    if (listRef.current && !isProgrammaticChange.current) {
      listRef.current.scrollToIndex(currentPageNumber - 1);
    }
    isProgrammaticChange.current = false;
  }, [listRef, currentPageNumber]);

  useEffect(() => {
    setIsZoomActive(false);
    setIsSnipActive(false);
    setIsCropActive(false);
    cancelSnip();
  }, [cancelSnip, selectedPageId, currentPageNumber]);

  useEffect(() => {
    if (!recognizeModalOpen) {
      setPageImageUrl(undefined);
      setPageRotation(0);
    }
  }, [recognizeModalOpen]);

  // Track if we should clear selection when the selected page goes off screen
  // This is triggered when URL navigation happens (e.g., clicking left sidebar)
  const shouldClearOnOffscreenRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      searchParams.pageId &&
      selectedPageId &&
      searchParams.pageId !== selectedPageId
    ) {
      // Don't clear immediately - wait until the selected page is off screen
      shouldClearOnOffscreenRef.current = selectedPageId;
    }
  }, [searchParams.pageId, selectedPageId]);

  const handleAddAnnotationClick = useCallback(
    async (pageId: string, positionX: number, positionY: number) => {
      if (!project?.id) return;
      try {
        await createAnnotation({
          pageId,
          body: {
            content: "",
            color: annotationColor || DEFAULT_ANNOTATION_COLOR,
            positionX,
            positionY,
          },
        });
        setIsAddingAnnotation(false);
      } catch {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "error",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: "pageAnnotations.createError",
            defaultMessage: "Failed to add note. Please try again.",
          }),
        });
      }
    },
    [
      project?.id,
      annotationColor,
      createAnnotation,
      setIsAddingAnnotation,
      toast,
      intl,
    ]
  );

  const handleAnnotationContentChange = useCallback(
    async (annotationId: string, content: string) => {
      try {
        await updateAnnotation({ annotationId, body: { content } });
      } catch {
        // Silent fail for content updates - debounced saves
      }
    },
    [updateAnnotation]
  );

  const handleAnnotationColorChange = useCallback(
    async (annotationId: string, color: string) => {
      try {
        await updateAnnotation({ annotationId, body: { color } });
      } catch {
        // Silent fail for color changes
      }
    },
    [updateAnnotation]
  );

  const handleAnnotationIconChange = useCallback(
    async (annotationId: string, icon: string) => {
      try {
        await updateAnnotation({ annotationId, body: { icon } });
      } catch {
        // Silent fail for icon changes
      }
    },
    [updateAnnotation]
  );

  const handleAnnotationTitleChange = useCallback(
    async (annotationId: string, title: string) => {
      try {
        await updateAnnotation({ annotationId, body: { title } });
      } catch {
        // Silent fail for title changes
      }
    },
    [updateAnnotation]
  );

  const handleAnnotationPositionChange = useCallback(
    async (annotationId: string, positionX: number, positionY: number) => {
      try {
        await updateAnnotation({
          annotationId,
          body: {
            positionX,
            positionY,
          },
        });
      } catch {
        // Silent fail for position changes
      }
    },
    [updateAnnotation]
  );

  const handleAnnotationDelete = useCallback(
    async (annotationId: string) => {
      try {
        await deleteAnnotation({ annotationId });
      } catch {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "error",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: "pageAnnotations.deleteError",
            defaultMessage: "Failed to delete note. Please try again.",
          }),
        });
      }
    },
    [deleteAnnotation, toast, intl]
  );

  const handleSelectedPageOffscreen = useCallback(() => {
    // Clear selection when the selected page goes completely off screen
    // This happens either due to URL navigation or user scrolling
    if (selectedPageId) {
      logger.info("DocViewer: Clearing selection - page went off screen", {
        selectedPageId,
      });
      cancelSnip();
      setIsSnipActive(false);
      setIsCropActive(false);
      clearSelectedPages();
      setIsAddingAnnotation(false);
      shouldClearOnOffscreenRef.current = null;
    }
  }, [cancelSnip, clearSelectedPages, selectedPageId, setIsAddingAnnotation]);

  return (
    <div className="h-screen overflow-hidden relative">
      {isDoclistOutdated && (
        <DoclistOutdatedDialog
          open={isDoclistOutdated}
          setOpen={setIsDoclistOutdated}
          onCancel={() => setIsDoclistOutdated(false)}
          onUpdate={handleDoclistUpdate}
        />
      )}

      <div ref={containerRef} className="bg-muted h-full relative">
        <DocViewerList
          ref={listRef}
          pages={pageItems}
          selectedPageId={selectedPageId}
          togglePageSelection={togglePageSelection}
          onScroll={handleCurrentPageChange}
          isZoomActive={isZoomActive}
          isSnipActive={isSnipActive}
          onSelectedPageOffscreen={handleSelectedPageOffscreen}
          organizationId={organizationId}
          projectId={project?.id}
          snipState={snipState}
          onSnipMouseDown={snipHandlers.onMouseDown}
          onSnipMouseMove={snipHandlers.onMouseMove}
          onSnipMouseUp={snipHandlers.onMouseUp}
          onSnipHoverMove={snipHandlers.onHoverMove}
          isCropActive={isCropActive}
          getAnnotationsForPage={getAnnotationsForPage}
          onAnnotationContentChange={handleAnnotationContentChange}
          onAnnotationColorChange={handleAnnotationColorChange}
          onAnnotationIconChange={handleAnnotationIconChange}
          onAnnotationTitleChange={handleAnnotationTitleChange}
          onAnnotationPositionChange={handleAnnotationPositionChange}
          onAnnotationDelete={handleAnnotationDelete}
          isViewOnly={isViewOnly}
          isAddingAnnotation={isAddingAnnotation}
          onAddAnnotationClick={handleAddAnnotationClick}
        />

        {/* Page Number Panel and Search */}
        <div className="absolute top-2 left-0 right-0 flex justify-center items-center gap-2 z-50">
          {totalPages > 1 && (
            <PageNumberPanel
              currentPage={currentPageNumber}
              totalPages={totalPages}
              onPageChange={handlePageChangeFromPanel}
            />
          )}
          <DocumentSearchPanel
            organizationId={organizationId}
            projectId={caseId}
            documentId={documentId ?? undefined}
            onNavigateToPage={handleSearchNavigateToPage}
          />
        </div>

        {/* Selected Page Panel */}
        <AnimatePresence>
          {selectedPageId && (
            <div
              className={`absolute left-0 right-0 flex justify-center z-50 ${isMobile ? "bottom-20" : "bottom-4"}`}
            >
              <SelectedPagePanel
                isViewOnly={isViewOnly}
                unselectPages={() => {
                  cancelSnip();
                  setIsSnipActive(false);
                  setIsCropActive(false);
                  clearSelectedPages();
                  setIsAddingAnnotation(false);
                }}
                onRotateClicked={handleRotateSelectedPage}
                onDeleteClicked={handleDeleteSelectedPage}
                onRecognizeClicked={handleRecognizeSelectedPage}
                onZoomClicked={handleToggleZoom}
                onHighlightClicked={handleToggleHighlight}
                onCropClicked={handleToggleCrop}
                onAddNoteClicked={() => {
                  setIsAddingAnnotation(!isAddingAnnotation);
                  if (!isAddingAnnotation) {
                    setIsSnipActive(false);
                    cancelSnip();
                    setIsCropActive(false);
                  }
                }}
                selectedPageNumber={selectedPageNumber}
                isRotateLoading={isRotating}
                isRecognizeLoading={isPageContentLoading}
                isZoomActive={isZoomActive}
                isAddingAnnotation={isAddingAnnotation}
                isHighlightActive={isSnipActive}
                isCropActive={isCropActive}
              />
            </div>
          )}
        </AnimatePresence>
      </div>

      {recognizeModalOpen && (
        <MarkdownContentModal
          title={modalTitle}
          content={
            isPageContentLoading || isContentLoading
              ? "Loading content..."
              : pageContentError
                ? "Error loading content. This page may not have processed content available yet."
                : pageContentData?.content ||
                  "No content available for this page."
          }
          filename={pageContentData?.filename}
          authorName={pageContentData?.authorName}
          uploadDate={pageContentData?.uploadDate}
          open={recognizeModalOpen}
          onOpenChange={setRecognizeModalOpen}
          copyEventName="app/doc_viewer/recognize_modal/copy_all.button/click"
          exportOpenEventName="app/doc_viewer/recognize_modal/export.button/click"
          exportEventName="app/doc_viewer/recognize_modal/export_menu_open/export_action.link/click"
          terms={[]}
          pageImageUrl={pageImageUrl}
          pageRotation={pageRotation}
          organizationId={organizationId}
          projectId={project?.id}
          pageId={selectedPageId ?? undefined}
          onContentUpdate={handleContentUpdate}
          isViewOnly={isViewOnly}
        />
      )}
    </div>
  );
};
